import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';

import { getMemberStats, onMembersChanged, validateApiResponse } from '../../../services';
import { mapApiMemberStatsToUi } from '../utils/mapApiMemberToUi';

/**
 * Numbers of the Members header cards. Refetches when a member mutation (invite, revoke,
 * remove, status) happens anywhere in the app, without flashing the skeleton again.
 *
 * @param {object} [options]
 * @param {() => Promise<object>} [options.loadStats] Injectable loader (defaults to the mock service).
 * @returns {{ stats: object|null, isLoading: boolean, error: string|null, refetch: () => Promise<void> }}
 */
export const useMemberStats = ({ loadStats = getMemberStats } = {}) => {
  const { t } = useTranslation('people-access');
  const [state, setState] = useState({ stats: null, isLoading: true, error: null });
  const fetchIdRef = useRef(0);
  const mountedRef = useRef(true);

  const refetch = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    try {
      const res = await loadStats();
      const { body } = validateApiResponse(res, t('members.stats.loadError'));
      if (!mountedRef.current || fetchId !== fetchIdRef.current) return;
      setState({ stats: mapApiMemberStatsToUi(body), isLoading: false, error: null });
    } catch (err) {
      if (!mountedRef.current || fetchId !== fetchIdRef.current) return;
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: err?.message || t('members.stats.loadError'),
      }));
    }
  }, [loadStats, t]);

  useEffect(() => {
    mountedRef.current = true;
    refetch();
    const off = onMembersChanged(refetch);
    return () => {
      mountedRef.current = false;
      off();
    };
  }, [refetch]);

  return useMemo(() => ({ ...state, refetch }), [state, refetch]);
};
